import { useLingui } from '@lingui/react/macro';
import { X } from 'lucide-react';
import { Dialog as DialogPrimitive } from 'radix-ui';
import type { ComponentProps } from 'react';

import { Button } from './button';
import { cn } from './cn';

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;
export const DialogPortal = DialogPrimitive.Portal;

export function DialogOverlay({ className, ...props }: ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        'fixed inset-0 z-50 bg-scrim data-[state=closed]:animate-fade-out data-[state=open]:animate-fade-in',
        className,
      )}
      {...props}
    />
  );
}

/**
 * Largura do diálogo (UUX §14): `confirm` (até 520 px) para confirmação curta, prévia e step-up;
 * `form` (70% da tela, até 1280 px) para formulário e fluxo, com a grade de campos em duas colunas;
 * `full` (quase toda a janela; tela cheia abaixo de 768 px, U12) para conteúdo que precisa de espaço
 * (tabela inteira, resposta longa): cabeçalho e corpo, e quem rola é o corpo.
 */
export type DialogSize = 'confirm' | 'form' | 'full';

const SIZE: Record<DialogSize, string> = {
  confirm: 'w-[calc(100vw-32px)] max-w-[32.5rem]',
  // Abaixo de 640 px fica igual ao confirm (100% − 32 px); entre 640 e 960 px nunca abaixo de 672 px.
  form: 'w-[min(calc(100vw-32px),max(70vw,42rem))] max-w-[80rem]',
  full: 'h-[calc(100dvh-48px)] w-[calc(100vw-48px)] max-w-[100rem] grid-cols-[minmax(0,1fr)] grid-rows-[auto_minmax(0,1fr)] overflow-hidden max-md:inset-0 max-md:h-dvh max-md:max-h-none max-md:w-screen max-md:translate-x-0 max-md:translate-y-0 max-md:rounded-none max-md:border-0 max-md:px-4 max-md:pt-[calc(env(safe-area-inset-top,0px)+1rem)] max-md:pb-[env(safe-area-inset-bottom,0px)]',
};

export type DialogContentProps = ComponentProps<typeof DialogPrimitive.Content> & {
  /** Botão de fechar no canto. Padrão: sim. */
  readonly showClose?: boolean;
  /** Padrão: `confirm`. */
  readonly size?: DialogSize;
};

/** Diálogo modal centralizado: foco preso, Esc e clique no fundo fecham (Radix). É o contêiner `dialog` da `FieldGrid`. */
export function DialogContent({ className, children, showClose = true, size = 'confirm', ...props }: DialogContentProps) {
  const { t } = useLingui();
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        data-size={size}
        className={cn(
          '@container/dialog fixed top-1/2 left-1/2 z-50 grid max-h-[calc(100dvh-32px)] -translate-x-1/2 -translate-y-1/2 gap-4 overflow-y-auto',
          SIZE[size],
          'rounded-xl border border-border bg-popover p-6 text-popover-foreground shadow-3 outline-none',
          'data-[state=closed]:animate-pop-out data-[state=open]:animate-pop-in',
          className,
        )}
        {...props}
      >
        {children}
        {showClose ? (
          <DialogPrimitive.Close asChild>
            <Button
              variant="ghost"
              size="icon"
              aria-label={t`Fechar`}
              className={cn('absolute top-3 right-3 size-8 text-muted-foreground', size === 'full' && 'max-md:top-[calc(env(safe-area-inset-top,0px)+0.5rem)] max-md:size-11')}
            >
              <X aria-hidden />
            </Button>
          </DialogPrimitive.Close>
        ) : null}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

export function DialogHeader({ className, ...props }: ComponentProps<'div'>) {
  return <div data-slot="dialog-header" className={cn('grid gap-1.5 pr-8', className)} {...props} />;
}

export function DialogFooter({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)}
      {...props}
    />
  );
}

export function DialogTitle({ className, ...props }: ComponentProps<typeof DialogPrimitive.Title>) {
  return <DialogPrimitive.Title data-slot="dialog-title" className={cn('text-lg font-semibold', className)} {...props} />;
}

export function DialogDescription({ className, ...props }: ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn('text-sm text-muted-foreground', className)}
      {...props}
    />
  );
}
